import {lazy, Suspense, useEffect} from 'react';
import {Route, Routes, useNavigate, useLocation, Navigate} from 'react-router-dom';
import {LoadingAnimation} from '../components/loading-animation/LoadingAnimation';
import {useAppDispatch, useAppSelector} from '../hooks';
import {AdminWebRoute} from '../routes/AdminWebRoute';
import {ProtectedUserRoute} from '../routes/ProtectedUserRoute';
import {ProtectedAdminRoute} from '../routes/ProtectedAdminRoute';
import {RootRoute} from '../routes/RootRoute';
// Keep frequently accessed routes as regular imports for faster initial load
// These are the landing pages users see every time they open the app
import {SceneAdminHome} from '../scenes/auth/admin/SceneAdminHome';
import {SceneDashboard} from '../scenes/auth/admin/dashboard/SceneDashboard';
import {SceneEmployeeHome} from '../scenes/employee-dashboard/SceneEmployeeHome';
import {SceneEmployeeHomeInternal} from '../scenes/employee-dashboard/SceneEmployeeHomeInternal';
// GetStarted is also kept as regular import since it's part of first-time user flow
import {SceneGetStarted} from '../scenes/get-started/SceneGetStarted';
import {useAuth} from '../store/AuthProvider';
import {fetchCompany} from '../store/company/CompanyThunks';
import {
  fetchUserDetails,
  initializeUserLanguage,
} from '../store/user/UserThunks';
import {
  validateReturnUrlPermissions,
  getFallbackRoute,
} from '../utils/auth/validateReturnUrlPermissions';
import {updateUserLastSignIn} from '../utils/user/updateUserLastSignIn';
import {checkBasicInfoComplete} from '../utils/user/checkBasicInfoComplete';
// Lazy load all other scenes for code splitting

// Login is lazy-loaded since users only see it once (or rarely)
const SceneLogin = lazy(() =>
  import('../scenes/login/SceneLogin').then(m => ({
    default: m.SceneLogin,
  })),
);

// Admin Scenes (other than landing pages)
const SceneEventsOverview = lazy(() =>
  import(
    /* webpackChunkName: "admin" */ '../scenes/auth/admin/events-overview/SceneEventsOverview'
  ).then(m => ({
    default: m.SceneEventsOverview,
  })),
);
const SceneEmployeesOverview = lazy(() =>
  import(
    /* webpackChunkName: "admin" */ '../scenes/auth/admin/employees-overview/SceneEmployeesOverview'
  ).then(m => ({
    default: m.SceneEmployeesOverview,
  })),
);
const SceneCreateEvent = lazy(() =>
  import(
    /* webpackChunkName: "admin" */ '../scenes/auth/admin/create-event/SceneCreateEvent'
  ).then(m => ({
    default: m.SceneCreateEvent,
  })),
);
const SceneEventDetails = lazy(() =>
  import(
    /* webpackChunkName: "admin" */ '../scenes/auth/admin/events-overview/event-details/SceneEventDetails'
  ).then(m => ({
    default: m.SceneEventDetails,
  })),
);
const SceneInterestAnalytics = lazy(() =>
  import(
    /* webpackChunkName: "admin" */ '../scenes/auth/admin/interest-analytics/SceneInterestAnalytics'
  ).then(m => ({
    default: m.SceneInterestAnalytics,
  })),
);
const SceneEventAnalytics = lazy(() =>
  import(
    /* webpackChunkName: "admin" */ '../scenes/auth/admin/event-analytics/SceneEventAnalytics'
  ).then(m => ({
    default: m.SceneEventAnalytics,
  })),
);
const SceneSettings = lazy(() =>
  import(
    /* webpackChunkName: "settings" */ '../scenes/settings/SceneSettings'
  ).then(m => ({
    default: m.SceneSettings,
  })),
);
const SceneSettingsIntegrations = lazy(() =>
  import(
    /* webpackChunkName: "settings" */ '../scenes/settings/SceneSettingsIntegrations'
  ).then(m => ({
    default: m.SceneSettingsIntegrations,
  })),
);
const SceneSettingsGeneral = lazy(() =>
  import(
    /* webpackChunkName: "settings" */ '../scenes/settings/SceneSettingsGeneral'
  ).then(m => ({
    default: m.SceneSettingsGeneral,
  })),
);
const SceneSettingsAdminManagement = lazy(() =>
  import(
    /* webpackChunkName: "settings" */ '../scenes/settings/SceneSettingsAdminManagement'
  ).then(m => ({
    default: m.SceneSettingsAdminManagement,
  })),
);

// Employee Scenes (other than landing pages)
const SceneEmployeePastEvents = lazy(() =>
  import(
    /* webpackChunkName: "employee" */ '../scenes/employee-dashboard/SceneEmployeePastEvents'
  ).then(m => ({
    default: m.SceneEmployeePastEvents,
  })),
);
const SceneColleaguesOverview = lazy(() =>
  import(
    /* webpackChunkName: "employee" */ '../scenes/employee-dashboard/colleagues-overview/SceneColleaguesOverview'
  ).then(m => ({
    default: m.SceneColleaguesOverview,
  })),
);
const SceneUserProfile = lazy(() =>
  import(
    /* webpackChunkName: "employee" */ '../scenes/employee-dashboard/user-profile/SceneUserProfile'
  ).then(m => ({
    default: m.SceneUserProfile,
  })),
);
const SceneConnectDetails = lazy(() =>
  import(
    /* webpackChunkName: "employee" */ '../scenes/employee-dashboard/activity/connect/SceneConnectDetails'
  ).then(m => ({
    default: m.SceneConnectDetails,
  })),
);
const SceneGroupDetails = lazy(() =>
  import(
    /* webpackChunkName: "employee" */ '../scenes/employee-dashboard/activity/group/SceneGroupDetails'
  ).then(m => ({
    default: m.SceneGroupDetails,
  })),
);

// Hubbl Creation Scenes
const SceneCreateHubbl = lazy(() =>
  import(
    /* webpackChunkName: "create-hubbl" */ '../scenes/create-hubbl/SceneCreateHubbl'
  ).then(m => ({
    default: m.SceneCreateHubbl,
  })),
);
const SceneCreateHubblGroup = lazy(() =>
  import(
    /* webpackChunkName: "create-hubbl" */ '../scenes/create-hubbl/SceneCreateHubblGroup'
  ).then(m => ({
    default: m.SceneCreateHubblGroup,
  })),
);
const SceneCreateHubblConnect = lazy(() =>
  import(
    /* webpackChunkName: "create-hubbl" */ '../scenes/create-hubbl/SceneCreateHubblConnect'
  ).then(m => ({
    default: m.SceneCreateHubblConnect,
  })),
);

// Onboarding Scenes
const SceneOnboardingBasicInfo = lazy(() =>
  import(
    /* webpackChunkName: "onboarding" */ '../scenes/onboarding/SceneOnboardingBasicInfo'
  ).then(m => ({
    default: m.SceneOnboardingBasicInfo,
  })),
);
const SceneOnboardingInterests = lazy(() =>
  import(
    /* webpackChunkName: "onboarding" */ '../scenes/onboarding/SceneOnboardingInterests'
  ).then(m => ({
    default: m.SceneOnboardingInterests,
  })),
);
const SceneOnboardingAddPhoto = lazy(() =>
  import(
    /* webpackChunkName: "onboarding" */ '../scenes/onboarding/SceneOnboardingAddPhoto'
  ).then(m => ({
    default: m.SceneOnboardingAddPhoto,
  })),
);

// Other Scenes
const SceneEventEndpoint = lazy(() =>
  import(
    /* webpackChunkName: "public" */ '../scenes/event/SceneEventEndpoint'
  ).then(m => ({
    default: m.SceneEventEndpoint,
  })),
);
const SceneInvited = lazy(() =>
  import(
    /* webpackChunkName: "public" */ '../scenes/invitation/SceneInvited'
  ).then(m => ({
    default: m.SceneInvited,
  })),
);
const SceneSignUpVerifyEmail = lazy(() =>
  import(
    /* webpackChunkName: "public" */ '../scenes/auth/SceneSignUpVerifyEmail'
  ).then(m => ({
    default: m.SceneSignUpVerifyEmail,
  })),
);
const SceneCalendarOAuthCallback = lazy(() =>
  import(
    /* webpackChunkName: "public" */ '../scenes/settings/SceneCalendarOAuthCallback'
  ).then(m => ({
    default: m.SceneCalendarOAuthCallback,
  })),
);

// Employee Settings
const SceneEmployeeSettings = lazy(() =>
  import(
    /* webpackChunkName: "employee-settings" */ '../scenes/employee-settings/SceneEmployeeSettings'
  ).then(m => ({
    default: m.SceneEmployeeSettings,
  })),
);
const SceneEmployeeSettingsGeneral = lazy(() =>
  import(
    /* webpackChunkName: "employee-settings" */ '../scenes/employee-settings/SceneEmployeeSettingsGeneral'
  ).then(m => ({
    default: m.SceneEmployeeSettingsGeneral,
  })),
);
const SceneEmployeeSettingsProfile = lazy(() =>
  import(
    /* webpackChunkName: "employee-settings" */ '../scenes/employee-settings/SceneEmployeeSettingsProfile'
  ).then(m => ({
    default: m.SceneEmployeeSettingsProfile,
  })),
);

// Bottom Sheets (keep separate or group with parent)
const SceneBottomSheetCreateEventAddEditEvaluationQuestions = lazy(() =>
  import(
    /* webpackChunkName: "admin-modals" */ '../scenes/auth/admin/create-event/add-questions/SceneBottomSheetCreateEventAddEditEvaluationQuestions'
  ).then(m => ({
    default: m.SceneBottomSheetCreateEventAddEditEvaluationQuestions,
  })),
);
const SceneBottomSheetCreateEventAddEditQuestions = lazy(() =>
  import(
    /* webpackChunkName: "admin-modals" */ '../scenes/auth/admin/create-event/add-questions/SceneBottomSheetCreateEventAddEditQuestions'
  ).then(m => ({
    default: m.SceneBottomSheetCreateEventAddEditQuestions,
  })),
);
const SceneBottomSheetCreateEventVisibility = lazy(() =>
  import(
    /* webpackChunkName: "admin-modals" */ '../scenes/auth/admin/create-event/add-visibility/SceneBottomSheetCreateEventVisibility'
  ).then(m => ({
    default: m.SceneBottomSheetCreateEventVisibility,
  })),
);
const SceneBottomSheetCreateHubblVisibilityFilters = lazy(() =>
  import(
    /* webpackChunkName: "create-hubbl-modals" */ '../scenes/create-hubbl/visibility-filters/SceneBottomSheetCreateHubblVisibilityFilters'
  ).then(m => ({
    default: m.SceneBottomSheetCreateHubblVisibilityFilters,
  })),
);
const SceneBottomSheetCreateHubblUserInvitations = lazy(() =>
  import(
    /* webpackChunkName: "create-hubbl-modals" */ '../scenes/create-hubbl/user-invitations/SceneBottomSheetCreateHubblUserInvitations'
  ).then(m => ({
    default: m.SceneBottomSheetCreateHubblUserInvitations,
  })),
);

// Component to handle /login redirect - just redirects to root
const LoginRedirect = () => {
  return <Navigate to={RootRoute.LogIn} replace />;
};

export const RootNavigator = () => {
  const {user, isLoading, isAdmin, isAdminView} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector(state => state.user.userDetails);

  useEffect(() => {
    // Track user sign in for automatic login
    user && updateUserLastSignIn();
  }, [user]);

  // Initialize user language when user details are loaded
  useEffect(() => {
    if (userDetails) {
      dispatch(initializeUserLanguage(userDetails));
    }
  }, [userDetails, dispatch]);

  useEffect(() => {
    if (!isLoading && user) {
      const currentPath = window.location.pathname;
      const currentFullUrl = window.location.pathname + window.location.search;
      const params = new URLSearchParams(location.search);
      const returnUrl = params.get('returnUrl');

      dispatch(fetchUserDetails());
      dispatch(fetchCompany());

      // CRITICAL: Check if user is in get-started flow (creating company)
      if (userDetails && userDetails.companyId === 'UNKNOWN_COMPANY') {
        if (
          currentPath !== RootRoute.GetStarted &&
          currentPath !== RootRoute.SignUpVerifyEmail
        ) {
          navigate(RootRoute.GetStarted, {replace: true});
          return;
        }
        return; // Don't proceed with other checks
      }

      // Check if user just completed onboarding
      const onboardingJustCompleted = localStorage.getItem(
        'onboardingJustCompleted',
      );

      // Onboarding check - only redirect if userDetails is loaded and user isn't onboarded
      // Skip redirect if user just completed onboarding (prevents race condition loop)
      if (
        userDetails &&
        !userDetails.isOnboarded &&
        !onboardingJustCompleted &&
        currentPath !== RootRoute.OnboardingBasicInfo &&
        currentPath !== RootRoute.OnboardingInterests &&
        currentPath !== RootRoute.OnboardingAddPhoto &&
        currentPath !== RootRoute.SignUpVerifyEmail
      ) {
        // Check if basic info is complete
        if (!checkBasicInfoComplete(userDetails)) {
          // Save the current full URL as return URL (or use returnUrl if provided)
          const urlToSave = returnUrl || currentFullUrl;
          if (urlToSave && urlToSave !== RootRoute.LogIn) {
            localStorage.setItem('onboardingReturnUrl', urlToSave);
          }
          navigate(RootRoute.OnboardingBasicInfo, {replace: true});
          return;
        }

        // Mark that we've shown the reminder in this session
        sessionStorage.setItem('interestsReminderShown', 'true');

        // Save the current full URL as return URL (or use returnUrl if provided)
        const urlToSave = returnUrl || currentFullUrl;
        if (urlToSave && urlToSave !== RootRoute.LogIn) {
          localStorage.setItem('onboardingReturnUrl', urlToSave);
        }
        navigate(RootRoute.OnboardingInterests, {replace: true});
        return;
      }

      // Clear the onboarding flag after navigation (if it was set)
      if (onboardingJustCompleted) {
        localStorage.removeItem('onboardingJustCompleted');
      }

      if (currentPath === RootRoute.LogIn) {
        if (returnUrl) {
          // Check if user has permission for the return URL
          if (validateReturnUrlPermissions(returnUrl, isAdmin || false)) {
            navigate(returnUrl, {replace: true});
          } else {
            // Redirect to appropriate dashboard if no permission
            navigate(getFallbackRoute(isAdmin || false, isAdminView), {
              replace: true,
            });
          }
        } else {
          // If no return URL, redirect to appropriate dashboard based on user type
          navigate(getFallbackRoute(isAdmin || false, isAdminView), {
            replace: true,
          });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, isAdminView, isLoading, dispatch, userDetails, location]);

  if (isLoading) {
    // Show loading screen while checking authentication
    return <LoadingAnimation />;
  }

  return (
    <Suspense fallback={<LoadingAnimation />}>
      <Routes>
        <Route path="/login" element={<LoginRedirect />} />
        <Route path={RootRoute.LogIn} element={<SceneLogin />} />
        <Route path={RootRoute.GetStarted} element={<SceneGetStarted />} />
        <Route path={RootRoute.Invited} element={<SceneInvited />} />

        {/* Calendar OAuth callback - public route (secured by state parameter) */}
        <Route
          path="/calendar/callback"
          element={<SceneCalendarOAuthCallback />}
        />

        {/* Employee routes - all nested under SceneEmployeeHome */}
        <Route element={<ProtectedUserRoute />}>
          <Route path="/employee/*" element={<SceneEmployeeHome />}>
            <Route path="home" element={<SceneEmployeeHomeInternal />} />
            <Route path="past-events" element={<SceneEmployeePastEvents />} />
            <Route
              path="colleagues-overview"
              element={<SceneColleaguesOverview />}
            />
            <Route path="create-hubbl" element={<SceneCreateHubbl />} />

            <Route
              path="create-hubbl/connect"
              element={<SceneCreateHubblConnect />}>
              <Route
                path="visibility-filters"
                element={<SceneBottomSheetCreateHubblVisibilityFilters />}
              />
              <Route
                path="user-invitations"
                element={<SceneBottomSheetCreateHubblUserInvitations />}
              />
            </Route>

            <Route
              path="create-hubbl/group"
              element={<SceneCreateHubblGroup />}>
              <Route
                path="visibility-filters"
                element={<SceneBottomSheetCreateHubblVisibilityFilters />}
              />
              <Route
                path="user-invitations"
                element={<SceneBottomSheetCreateHubblUserInvitations />}
              />
            </Route>

            <Route path="user/:userId" element={<SceneUserProfile />} />

            {/* Detail routes for connect and group */}
            <Route
              path="connect/:connectId"
              element={<SceneConnectDetails />}
            />
            <Route path="group/:groupId" element={<SceneGroupDetails />} />

            {/* Nested event route */}
            <Route path="event/:eventId" element={<SceneEventEndpoint />} />

            {/* Employee settings routes */}
            <Route path="settings" element={<SceneEmployeeSettings />}>
              <Route index element={<SceneEmployeeSettingsGeneral />} />
              <Route
                path="general"
                element={<SceneEmployeeSettingsGeneral />}
              />
              <Route
                path="profile"
                element={<SceneEmployeeSettingsProfile />}
              />
            </Route>
          </Route>
        </Route>

        {/* Admin routes */}
        <Route element={<ProtectedAdminRoute />}>
          <Route path={RootRoute.AuthAdminHome} element={<SceneAdminHome />}>
            <Route index element={<SceneDashboard />} />
            <Route
              path={AdminWebRoute.Dashboard}
              element={<SceneDashboard />}
            />
            <Route
              path={AdminWebRoute.EventsOverview}
              element={<SceneEventsOverview />}
            />

            <Route
              path={`${AdminWebRoute.EventsOverview}/${AdminWebRoute.EventDetails}/:eventId`}
              element={<SceneEventDetails />}
            />
            <Route
              path={AdminWebRoute.EmployeesOverview}
              element={<SceneEmployeesOverview />}
            />
            <Route
              path={AdminWebRoute.CreateEvent}
              element={<SceneCreateEvent />}>
              <Route
                path={AdminWebRoute.CreateEventAddEditQuestions}
                element={<SceneBottomSheetCreateEventAddEditQuestions />}
              />
              <Route
                path={AdminWebRoute.CreateEventAddEditEvaluationQuestions}
                element={
                  <SceneBottomSheetCreateEventAddEditEvaluationQuestions />
                }
              />
              <Route
                path={AdminWebRoute.CreateEventAddVisibility}
                element={<SceneBottomSheetCreateEventVisibility />}
              />
            </Route>
            <Route
              path={AdminWebRoute.InterestAnalytics}
              element={<SceneInterestAnalytics />}
            />
            <Route
              path={AdminWebRoute.EventAnalytics}
              element={<SceneEventAnalytics />}
            />
            <Route path={AdminWebRoute.Settings} element={<SceneSettings />}>
              <Route index element={<SceneSettingsGeneral />} />
              <Route
                path={AdminWebRoute.SettingsGeneral}
                element={<SceneSettingsGeneral />}
              />
              <Route
                path={AdminWebRoute.SettingsIntegrations}
                element={<SceneSettingsIntegrations />}
              />
              <Route
                path={AdminWebRoute.SettingsAdminManagement}
                element={<SceneSettingsAdminManagement />}
              />
            </Route>
          </Route>
          {/* <Route
          path={RootRoute.AuthAdminLandingPage}
          element={<_SceneAdminLandingPage />}>
          <Route index element={<_SceneDashboard />} />

          <Route
            path={AdminWebRoute.Dashboard}
            element={<SceneDashboard />}></Route>
          <Route
            path={AdminWebRoute.Employees}
            element={<SceneEmployees />}></Route>
          <Route
            path={AdminWebRoute.AddInterestStatistic}
            element={<SceneAddInterestStatistic />}></Route>
          <Route
            path={AdminWebRoute.AddHubblSuggestion}
            element={<SceneAddHubblSuggestion />}></Route>

          <Route
            path="*"
            element={<Navigate to={AdminWebRoute.Dashboard} a />
        </Route> */}
        </Route>

        {/* Event, onboarding, and verification routes (protected user routes) */}
        <Route element={<ProtectedUserRoute />}>
          <Route
            path={RootRoute.SignUpVerifyEmail}
            element={<SceneSignUpVerifyEmail />}
          />
          <Route
            path={`${RootRoute.Event}/:eventId`}
            element={<SceneEventEndpoint />}
          />
          <Route
            path={RootRoute.OnboardingBasicInfo}
            element={<SceneOnboardingBasicInfo />}
          />
          <Route
            path={RootRoute.OnboardingInterests}
            element={<SceneOnboardingInterests />}
          />
          <Route
            path={RootRoute.OnboardingAddPhoto}
            element={<SceneOnboardingAddPhoto />}
          />
        </Route>

        {/** TODO: Add not found scene */}
        <Route path="*" Component={() => <>'404 Page Not Found'</>} />
      </Routes>
    </Suspense>
  );
};
